import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import Footer, { SUPPORT_EMAIL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../components/Footer";
import BottomNav from "../components/BottomNav";
import SEO from "../components/SEO";
import { Mail, Instagram, MessageSquare, HelpCircle, Handshake, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const TOPICS = [
  { id: "support", icon: HelpCircle, title: "Product support", desc: "Something isn't working, or a feature isn't clear.", subject: "Support · " },
  { id: "brand", icon: Handshake, title: "Brand enquiry", desc: "You want to run a campaign or need a bespoke quote.", subject: "Brand enquiry · " },
  { id: "creator", icon: MessageSquare, title: "Creator enquiry", desc: "You're a creator with a question about payouts, profile or growth.", subject: "Creator enquiry · " },
  { id: "trust", icon: ShieldCheck, title: "Trust, safety & disputes", desc: "Report a dispute, fake account, or platform abuse.", subject: "Trust & safety · " },
];

export default function Contact() {
  const compose = (topic) => {
    const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(topic.subject + "[your topic]")}&body=${encodeURIComponent("Hi Nexus team,\n\n")}`;
    window.location.href = url;
  };
  const copy = () => {
    navigator.clipboard?.writeText(SUPPORT_EMAIL);
    toast.success("Email copied to clipboard");
  };
  return (
    <div className="min-h-screen grain flex flex-col">
      <SEO
        title="Contact & Support · Nexus"
        description="Get in touch with the Nexus team. Email support, brand enquiries, creator help, and trust & safety."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Nexus — Contact",
          "email": SUPPORT_EMAIL,
          "sameAs": [INSTAGRAM_URL],
        }}
      />
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-6 w-full">
        <BackButton fallback="/" label="Back" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pt-4 pb-24 w-full flex-1 space-y-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <Mail className="h-4 w-4" /> Contact & support
          </div>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
            We're here to <span className="nexus-gradient-text">help you win.</span>
          </h1>
          <p className="text-zinc-400 mt-3 text-sm max-w-2xl">
            Reach the Nexus team any time. We reply to every message — usually within one working day.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass rounded-2xl p-6" data-testid="contact-email-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-2xl nexus-gradient flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Email</div>
                <div className="text-xs text-zinc-500">One working day reply, usually faster</div>
              </div>
            </div>
            <div className="text-base font-mono text-white bg-white/[0.03] rounded-xl p-3 hairline break-all">
              {SUPPORT_EMAIL}
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={() => (window.location.href = `mailto:${SUPPORT_EMAIL}`)}
                className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 flex-1"
                data-testid="contact-mailto-btn"
              >
                Send email
              </Button>
              <Button
                variant="outline"
                onClick={copy}
                className="bg-white/5 border-white/15 rounded-full text-white hover:bg-white/10 hover:text-white"
                data-testid="contact-copy-btn"
              >
                Copy
              </Button>
            </div>
          </div>

          <div className="glass rounded-2xl p-6" data-testid="contact-instagram-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] flex items-center justify-center">
                <Instagram className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">Instagram</div>
                <div className="text-xs text-zinc-500">DMs open — behind-the-scenes, launches & wins</div>
              </div>
            </div>
            <div className="text-base font-mono text-white bg-white/[0.03] rounded-xl p-3 hairline">
              {INSTAGRAM_HANDLE}
            </div>
            <Button
              onClick={() => window.open(INSTAGRAM_URL, "_blank")}
              className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90 w-full mt-3"
              data-testid="contact-instagram-btn"
            >
              Open on Instagram
            </Button>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Pick a topic — we'll route it to the right team</div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="contact-topics">
            {TOPICS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => compose(t)}
                  data-testid={`contact-topic-${t.id}`}
                  className="p-5 rounded-2xl bg-[#0C0C10] hairline text-left card-hover transition-colors hover:border-white/25 group"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-white/5 hairline flex items-center justify-center group-hover:bg-nexus-purple/20 transition-colors">
                      <Icon className="h-4 w-4 text-nexus-pink" />
                    </div>
                    <div className="font-display font-bold">{t.title}</div>
                  </div>
                  <div className="text-xs text-zinc-400">{t.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
      <BottomNav />
    </div>
  );
}
